import * as angular from 'angular';
import * as moment from 'moment';

import { ComponentDefinition } from '../../../models';
import { IConfigurationService, ISupportDetails, IStoreService, ILoginResult } from '../../../services';
import { StoreElement } from '../../../stateManagement';
import { ILoginData } from '../ILoginData';
import { ApiStatus } from './ApiStatus';

class LoginPageController implements angular.IComponentController, angular.IOnDestroy {
    static controllerId = 'Login';

    apiDate: Date;
    apiStatus: ApiStatus;
    apiUrl: string;
    apiVersion: string;
    daysToExpiration: number;
    emailAdmin: string;
    errorMessage: string;
    hideApiInfo: boolean;
    isContactingApi: boolean;
    isInDomain: boolean;
    isLicenseBlockedForMax: boolean;
    isLicenseCloseToExpiration: boolean;
    isLicenseCloseToMax: boolean;
    isLicenseExceededMax: boolean;
    isLicenseExpired: boolean;
    isLicenseValid: boolean;
    licenseExpiryDate: Date;
    maxNumberOfIdentifiers: number;
    numberOfIdentifiers: number;
    style: any = {};
    supportDetails: ISupportDetails;
    translationData: any;
    uiDate: Date;
    uiVersion: string;

    private _unsubscribe: () => void;

    static $inject = [
        '$stateParams',
        'configurationService',
        'storeService'
    ];

    constructor(
        private _$stateParams: angular.ui.IStateParamsService,
        private _configurationService: IConfigurationService,
        private _storeService: IStoreService
    ) {
    }

    $onInit() {
        this.apiStatus = { areSameDates: true, areSameVersions: true };
        this.errorMessage = this._$stateParams.message ? this._$stateParams.message : '';

        this._unsubscribe = this._storeService.register(
            store => { /* do nothing */ },
            [ StoreElement.ApiInformation, StoreElement.Authentication ]
        );

        this._configurationService
            .getConfiguration()
            .then(data => {
                this.apiUrl = data.apiUrl;
                this.emailAdmin = data.admin.email;
                this.supportDetails = data.supportDetails;
                this.hideApiInfo = data.hideApiInfo;
                if (data.backgroundUrl && !this.style.background) {
                    this.style = {
                        background: `url("${data.backgroundUrl}")`,
                        'background-size': 'cover'
                    };
                }
                this._configurationService
                    .getUiInformation()
                    .then(date => {
                        this.uiDate = date.date;
                        this.uiVersion = date.version;
                        this.isContactingApi = true;
                        return this._storeService.dispatchers().apiInformation.getApiInformation();
                    })
                    .then(info => {
                        this.apiDate = moment(info.buildDate).toDate();
                        this.apiVersion = info.version;
                        this.isLicenseValid = info.isLicenseValid;
                        this.numberOfIdentifiers = info.numberOfIdentifiers;
                        this.maxNumberOfIdentifiers = info.maxNumberOfIdentifiers;
                        this.licenseExpiryDate = moment(info.licenseValidityDate).toDate();
                        this.isInDomain = info.isInDomain;
                        const areSameVersions = this.apiVersion === this.uiVersion;
                        const areSameDates = !areSameVersions || moment(this.uiDate).startOf('d').isSame(moment(this.apiDate).startOf('d'));
                        this.apiStatus = {
                            areSameVersions: areSameVersions,
                            areSameDates: areSameDates
                        };
                        if (info.maxNumberOfIdentifiers > 0) {
                            this.isLicenseBlockedForMax =
                                info.numberOfIdentifiers > (info.maxNumberOfIdentifiers + 100);
                            this.isLicenseExceededMax = !this.isLicenseBlockedForMax &&
                                info.numberOfIdentifiers > (info.maxNumberOfIdentifiers);
                            this.isLicenseCloseToMax = !this.isLicenseBlockedForMax && !this.isLicenseExceededMax &&
                                info.numberOfIdentifiers > (info.maxNumberOfIdentifiers - 100);
                        }

                        if (!this.isLicenseValid) {
                            this.errorMessage = 'Your license is invalid.';
                        } else {
                            const duration = moment(info.licenseValidityDate).diff(moment());
                            const daysToExpiration = moment.duration(duration).as('d');
                            this.isLicenseCloseToExpiration = daysToExpiration < 30;
                            this.isLicenseExpired = daysToExpiration <= 0;
                            if (this.isLicenseExpired) {
                                this.errorMessage = 'Your license has expired.';
                            }
                            this.daysToExpiration = Math.floor(daysToExpiration);
                        }
                    })
                    .catch(_ => {
                        this.errorMessage = 'Could not retrieve the API build date. Check your API url or contact your administrator.';
                    })
                    .finally(() => this.isContactingApi = false);
            });
    }

    $onDestroy(): void {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    refresh() {
        window.location.reload();
    }

    login(loginData: ILoginData): angular.IPromise<ILoginResult> {
        return this._storeService.dispatchers().authentication.login(loginData);
    }
}

export const wtLoginPage: ComponentDefinition = {
    componentId: 'wtLoginPage',
    controller: LoginPageController,
    template: require('./loginPage.component.html'),
    controllerAs: 'vm',
    bindings: {
    }
};